// MyImpact — donor portal impact dashboard (CHG-1517 wave 2).
//
// /my-giving/impact. Consumes GET /api/v1/my-giving/impact. Per-org
// breakdown with per-program (designation) progress bars + your-given
// vs program-raised vs program-goal.
//
// Composition primitives: PageHeader + ProgressBar (existing).

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/composition/PageHeader";
import { ProgressBar } from "../components/composition/ProgressBar";
import { useFormatters } from "../contexts/IntlContext";
import {
  meImpactApi,
  type PortalImpact,
  type PortalImpactOrganization,
  type PortalImpactProgram,
} from "../api/me-impact";

const DESIG_TYPE_LABEL: Record<string, string> = {
  fund: "Fund",
  program: "Program",
  project: "Project",
  cause: "Cause",
  milestone: "Milestone",
};

function ProgramRow({
  program,
  currency,
}: {
  program: PortalImpactProgram;
  currency: string;
}) {
  const { formatCurrency } = useFormatters();
  const goal = program.program_goal_minor_units;
  const raised = program.program_raised_minor_units;
  const yourTotal = program.your_total_minor_units;
  const pct =
    goal > 0 ? Math.min(100, Math.round((raised / goal) * 100)) : 0;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        padding: "10px 0",
        borderBottom: "1px solid var(--color-border)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          fontSize: 14,
        }}
      >
        <span style={{ fontWeight: 500, color: "var(--color-text)" }}>
          {program.designation_name}
        </span>
        <span
          style={{ color: "var(--color-text-muted)", fontSize: 12 }}
          aria-label="designation type"
        >
          {DESIG_TYPE_LABEL[program.designation_type] ?? program.designation_type}
        </span>
      </div>
      <div style={{ fontSize: 13, color: "var(--color-text-muted)" }}>
        You: <strong>{formatCurrency(yourTotal, currency)}</strong>
        {program.your_donation_count > 0 ? (
          <span style={{ opacity: 0.6 }}>
            {" "}
            ({program.your_donation_count} gift
            {program.your_donation_count === 1 ? "" : "s"})
          </span>
        ) : null}
      </div>
      {goal > 0 ? (
        <ProgressBar
          value={pct}
          max={100}
          tone="g-mygiving"
          valueFormatted={`${formatCurrency(raised, currency)} of ${formatCurrency(goal, currency)} (${pct}%)`}
        />
      ) : (
        <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
          Program raised so far:{" "}
          <strong>{formatCurrency(raised, currency)}</strong>
        </div>
      )}
    </div>
  );
}

function OrgCard({ org }: { org: PortalImpactOrganization }) {
  const { formatCurrency } = useFormatters();
  return (
    <section
      className="card"
      aria-labelledby={`impact-org-${org.org_id}-title`}
      style={{
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          gap: 12,
        }}
      >
        <h2
          id={`impact-org-${org.org_id}-title`}
          style={{ fontSize: 16, fontWeight: 600, margin: 0 }}
        >
          <Link
            to={`/my-giving/org/${org.org_id}`}
            style={{ color: "var(--color-text)", textDecoration: "none" }}
          >
            {org.org_name}
          </Link>
        </h2>
        <div style={{ fontSize: 14, color: "var(--color-text-muted)" }}>
          You gave{" "}
          <strong style={{ color: "var(--color-text)" }}>
            {formatCurrency(org.your_total_minor_units, org.currency)}
          </strong>
        </div>
      </header>
      <div>
        {org.programs.length === 0 ? (
          <div
            style={{
              fontSize: 13,
              color: "var(--color-text-muted)",
              padding: "8px 0",
            }}
          >
            No designation-tagged giving yet for this organization.
          </div>
        ) : (
          org.programs.map((p) => (
            <ProgramRow
              key={p.designation_id}
              program={p}
              currency={org.currency}
            />
          ))
        )}
      </div>
    </section>
  );
}

function HeaderTotals({ data }: { data: PortalImpact }) {
  const { formatCurrency } = useFormatters();
  if (data.total_given_per_currency.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        gap: 16,
        flexWrap: "wrap",
        padding: "12px 16px",
        background: "var(--surface-2, #f6f7fb)",
        borderRadius: 8,
        marginBottom: 16,
      }}
    >
      <strong style={{ fontSize: 13 }}>Lifetime giving:</strong>
      {data.total_given_per_currency.map((t) => (
        <span key={t.currency} style={{ fontSize: 13 }}>
          <strong>{t.currency}:</strong>{" "}
          {formatCurrency(t.total_minor_units, t.currency)}
          <span style={{ opacity: 0.6 }}>
            {" "}
            ({t.count} gift{t.count === 1 ? "" : "s"})
          </span>
        </span>
      ))}
    </div>
  );
}

export function MyImpact() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["me", "impact"] as const,
    queryFn: meImpactApi.fetchMyImpact,
  });

  return (
    <main
      style={{
        maxWidth: 1280,
        margin: "0 auto",
        padding: "16px 24px 48px",
      }}
    >
      <PageHeader
        title="My impact"
        subtitle="Where your giving lands — totals + programs you've supported, by organization."
      />
      {isLoading ? (
        <div
          style={{
            padding: 32,
            color: "var(--color-text-muted)",
            fontSize: 14,
          }}
        >
          Loading impact…
        </div>
      ) : error ? (
        <div style={{ padding: 32, textAlign: "center" }}>
          <p style={{ fontSize: 14, color: "var(--color-text-muted)" }}>
            Failed to load impact.
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            style={{
              fontSize: 13,
              color: "var(--accent, #0066cc)",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Retry
          </button>
        </div>
      ) : !data || data.organizations.length === 0 ? (
        <div
          style={{
            padding: 48,
            textAlign: "center",
            color: "var(--color-text-muted)",
            fontSize: 14,
          }}
        >
          You haven't made any donations yet — once you do, your impact
          across every organization you support will appear here.
        </div>
      ) : (
        <>
          <HeaderTotals data={data} />
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {data.organizations.map((org) => (
              <OrgCard key={org.org_id} org={org} />
            ))}
          </div>
        </>
      )}
    </main>
  );
}
